import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield, ShieldCheck, ShieldAlert, Loader2, Key,
  RefreshCcw, AlertTriangle, Download, Copy, Check,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { BACKEND_URL } from '../../lib/backend';

const API = `${BACKEND_URL}/api`;

export function TwoFactorTab() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  // Action dialogs
  const [actionMode, setActionMode] = useState(null); // 'regenerate' | 'reset'
  const [actionCode, setActionCode] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  // New recovery codes after regenerate
  const [newCodes, setNewCodes] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/auth/totp/status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const openAction = (mode) => {
    setActionMode(mode);
    setActionCode('');
  };

  const closeAction = () => {
    setActionMode(null);
    setActionCode('');
  };

  const handleAction = async () => {
    if (!actionCode.trim()) {
      toast.error('Enter your current 6-digit code');
      return;
    }
    setActionSubmitting(true);
    try {
      if (actionMode === 'regenerate') {
        const { data } = await axios.post(
          `${API}/auth/totp/recovery-codes/regenerate`,
          { code: actionCode.trim() },
          { withCredentials: true }
        );
        setNewCodes(data.recovery_codes || []);
        toast.success('New recovery codes generated. Save them now.');
        await loadStatus();
        closeAction();
      } else if (actionMode === 'reset') {
        await axios.post(
          `${API}/auth/totp/disable`,
          { code: actionCode.trim() },
          { withCredentials: true }
        );
        toast.success('2FA reset. You will be redirected to re-enroll.');
        closeAction();
        // Forced re-enrolment happens via the AdminRoute guard
        navigate('/admin/2fa-setup');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const copyAllCodes = async () => {
    try {
      await navigator.clipboard.writeText(newCodes.join('\n'));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [
        'Tune4Tune Admin Recovery Codes\n',
        '==============================\n\n',
        `Generated: ${new Date().toISOString()}\n\n`,
        'Each code can be used only once if you lose access to your authenticator app.\n\n',
        newCodes.join('\n'),
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tune4tune-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-lime" />
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const remaining = status?.recovery_codes_remaining ?? 0;
  const lowOnCodes = enabled && remaining <= 3;

  return (
    <div className="space-y-6" data-testid="2fa-settings-tab">
      <div className="bg-surface border border-[#262626] rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${enabled ? 'bg-lime/10' : 'bg-yellow-500/10'}`}>
            {enabled ? <ShieldCheck className="w-6 h-6 text-lime" /> : <ShieldAlert className="w-6 h-6 text-yellow-500" />}
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold mb-1">Two-factor authentication</h2>
            <p className="text-sm text-[#A1A1AA]">
              {enabled
                ? 'Your admin account is protected by a TOTP authenticator app.'
                : 'Two-factor authentication is currently disabled. Re-enroll to protect your admin account.'}
            </p>
            {enabled && status?.enabled_at && (
              <p className="text-xs text-[#666] mt-2" data-testid="2fa-enabled-at">
                Enabled on {new Date(status.enabled_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {!enabled && (
        <div className="bg-surface border border-[#262626] rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Shield className="w-5 h-5 text-lime mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Enable two-factor authentication</h3>
              <p className="text-sm text-[#A1A1AA]">
                Set up an authenticator app to secure every admin sign-in.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/admin/2fa-setup')}
            className="btn-primary"
            data-testid="2fa-enroll-btn"
          >
            Set up 2FA
          </Button>
        </div>
      )}

      {enabled && (
        <>
          <div className="bg-surface border border-[#262626] rounded-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Key className="w-5 h-5 text-lime mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Recovery codes</h3>
                  <p className="text-sm text-[#A1A1AA]">
                    Single-use codes you can use if you lose access to your authenticator.
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div
                  className={`text-2xl font-mono font-bold ${lowOnCodes ? 'text-yellow-400' : 'text-lime'}`}
                  data-testid="2fa-codes-remaining"
                >
                  {remaining}
                </div>
                <div className="text-xs text-[#666]">remaining</div>
              </div>
            </div>
            {lowOnCodes && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-100">
                  You have {remaining === 0 ? 'no recovery codes left' : 'only a few recovery codes remaining'}. Regenerate now so you don't get locked out.
                </p>
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => openAction('regenerate')}
              className="mt-4 border-[#333]"
              data-testid="2fa-regenerate-btn"
            >
              <RefreshCcw className="w-4 h-4 mr-2" /> Regenerate recovery codes
            </Button>
          </div>

          <div className="bg-surface border border-[#262626] rounded-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1">Reset 2FA device</h3>
                <p className="text-sm text-[#A1A1AA]">
                  Disconnect your current authenticator and immediately re-enroll a new one (useful if you got a new phone).
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => openAction('reset')}
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
              data-testid="2fa-reset-btn"
            >
              Reset 2FA device
            </Button>
          </div>
        </>
      )}

      {/* Action confirmation dialog */}
      <AlertDialog open={!!actionMode} onOpenChange={(open) => !open && closeAction()}>
        <AlertDialogContent className="bg-[#0a0a0a] border-[#262626]" data-testid="2fa-action-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionMode === 'regenerate' ? 'Generate new recovery codes?' : 'Reset 2FA device?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionMode === 'regenerate'
                ? 'Your existing recovery codes will be invalidated. Enter your current authenticator code to continue.'
                : 'Your current device will be disconnected. You will be redirected to set up 2FA again right away. Enter your current authenticator code to confirm.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="action-code">Current 6-digit code</Label>
            <Input
              id="action-code"
              inputMode="numeric"
              value={actionCode}
              onChange={(e) => setActionCode(e.target.value)}
              placeholder="123 456"
              className="input-dark text-center text-xl tracking-[0.4em] font-mono"
              maxLength={8}
              data-testid="2fa-action-code-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#333]" data-testid="2fa-action-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleAction(); }}
              disabled={actionSubmitting}
              className={actionMode === 'reset' ? 'bg-red-500 hover:bg-red-600 text-white' : 'btn-primary'}
              data-testid="2fa-action-confirm"
            >
              {actionSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working...</> : (actionMode === 'reset' ? 'Reset & re-enroll' : 'Regenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New recovery codes display */}
      <AlertDialog open={!!newCodes} onOpenChange={(open) => !open && setNewCodes(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-[#262626]" data-testid="2fa-new-codes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Your new recovery codes</AlertDialogTitle>
            <AlertDialogDescription>
              Save these now. They <strong>will not</strong> be shown again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {newCodes && (
            <div className="grid grid-cols-2 gap-2 bg-[#050505] border border-[#262626] rounded-lg p-4 font-mono text-sm">
              {newCodes.map((c) => (
                <div key={c} className="text-lime">{c}</div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyAllCodes} className="border-[#333] flex-1">
              {copiedAll ? <><Check className="w-4 h-4 mr-2 text-lime" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy all</>}
            </Button>
            <Button variant="outline" onClick={downloadCodes} className="border-[#333] flex-1">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setNewCodes(null)}
              className="btn-primary w-full"
              data-testid="2fa-new-codes-done"
            >
              I've saved them
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
